/* eslint-disable @typescript-eslint/naming-convention */
import { makeApiCall } from "@cart-client/utils/Api";
import { ApiResponse } from "@cart-client/interfaces/Api";
import {
    ICouponsAPIResponse,
    GiftingCartApiResponse,
    GiftingLocalCart,
    GiftingCouponCode,
    IAppliedCouponCodes,
} from "@swiggy-private/cart-types";
import { getUpdateCartParams } from "@cart-client/helpers/ApiHelper";
import {
    isEmpty,
    CouponPageType,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    addAdditionalHeadersToPLAPI,
} from "@swiggy-private/checkout-core";
import { Ok, Err, Result } from "@swiggy-private/js-fp";

export type ResultCartData = Result<ApiResponse<GiftingCartApiResponse>, Error | ApiResponse>;

const ENDPOINT = {
    GET_CART: "/api/v3/gifting-get-cart",
    UPDATE_CART: "/api/v3/gifting-update-cart",
    UPDATE_COUPONS: "/api/v3/gifting-update-coupons",
    FETCH_COUPONS: "/api/v3/fetch-coupons",
};

export interface UpdateCartParams {
    [key: string]: unknown;
}

export async function getGiftingCart(apiHost: string): Promise<ApiResponse<GiftingCartApiResponse>> {
    const response = await makeApiCall<GiftingCartApiResponse>({ apiHost, endpoint: ENDPOINT.GET_CART });
    return response;
}

export async function updateGiftingCart(
    apiHost: string,
    params: GiftingLocalCart,
): Promise<ApiResponse<GiftingCartApiResponse>> {
    const response = await makeApiCall<GiftingCartApiResponse>({
        apiHost,
        endpoint: ENDPOINT.UPDATE_CART,
        bodyParams: getUpdateCartParams(params),
        options: { method: "POST" },
    });
    return response;
}

export async function updateGiftingCoupons({
    apiHost,
    appliedCouponCodes,
    couponCode,
    localCart,
}: {
    apiHost: string;
    appliedCouponCodes: IAppliedCouponCodes[];
    couponCode: string;
    localCart?: GiftingLocalCart | null;
}): Promise<ResultCartData> {
    // Convert IAppliedCouponCodes to GiftingCouponCode format
    const couponCodes: GiftingCouponCode[] = appliedCouponCodes.map((coupon) => ({
        code: coupon.code,
        businessLine: coupon.businessLine,
    }));

    // Add couponCode from input box to the array if it's not empty
    if (!isEmpty(couponCode)) {
        couponCodes.push({
            code: couponCode,
            businessLine: "",
        });
    }

    // Create a minimal GiftingLocalCart payload with coupon codes
    const giftingLocalCartPayload: GiftingLocalCart = { ...localCart, couponCodes };

    try {
        const response = await makeApiCall<GiftingCartApiResponse>({
            apiHost,
            endpoint: ENDPOINT.UPDATE_COUPONS,
            bodyParams: getUpdateCartParams(giftingLocalCartPayload),
            options: { method: "POST" },
        });

        if (!response) {
            throw new Error("Received empty data from cart details");
        }

        return Ok(response);
    } catch (err) {
        return Err(err) as ResultCartData;
    }
}

export async function fetchGiftingCouponListing(
    apiHost: string,
    params: {
        appliedCouponCodes?: IAppliedCouponCodes[];
    },
): Promise<ICouponsAPIResponse> {
    const response = await makeApiCall({
        apiHost,
        endpoint: ENDPOINT.FETCH_COUPONS,
        searchParams: {
            lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat),
            lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng),
            sub_page: CouponPageType.GIFTING_COUPON_LISTING,
        },
        bodyParams: params,
        options: { method: "POST", headers: addAdditionalHeadersToPLAPI() },
    });
    return response as ICouponsAPIResponse;
}
